import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PublicLayout } from '@/components/PublicLayout';
import { useAuth } from '@/lib/auth';
import { Kicker } from '@/components/Kicker';
import { ArrowRight, Package, FileText, Send, ShieldCheck, Users } from 'lucide-react';

function KPI({ label, value, testid, hint }) {
  return (
    <div className="pn-card p-6" data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-wide text-[#4A4D52]">{label}</div>
      <div className="mt-2 font-display text-3xl font-semibold text-[#14171C]">{value}</div>
      {hint && <div className="mt-1 text-xs text-[#4A4D52]">{hint}</div>}
    </div>
  );
}

export default function PortalDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const productsQ = useQuery({
    queryKey: ['products', 'portal'],
    queryFn: async () => (await api.get('/products')).data,
  });
  const requestsQ = useQuery({
    queryKey: ['requests', 'mine'],
    queryFn: async () => (await api.get('/requests/mine')).data,
  });

  const visible = productsQ.data?.count || 0;
  const requests = requestsQ.data?.items || [];
  const openRequests = requests.filter((r) => r.status === 'new').length;

  const docsCount = (productsQ.data?.items || []).reduce((a, p) => a + (p.documents_count || 0), 0);

  return (
    <PublicLayout>
      <section className="pn-container py-10">
        <Kicker>{t('brand.portal')}</Kicker>
        <h1 className="mt-3 font-display text-3xl font-semibold text-[#14171C] sm:text-4xl">{t('portal.welcome')}, {user?.full_name}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F6F8FB] px-3 py-1 text-xs">
            <Users className="h-3.5 w-3.5 text-[#4A4D52]" />
            {t('portal.linked_partner')}: <span className="font-medium text-[#14171C]">{user?.odoo_partner_name || t('portal.linked_partner_none')}</span>
          </span>
          {(user?.partner_category_names || []).length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBEADD] px-3 py-1 text-xs text-[#E2611C]">
              <ShieldCheck className="h-3.5 w-3.5" />
              {t('portal.my_categories')}: {(user.partner_category_names || []).join(', ')}
            </span>
          )}
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <KPI testid="portal-kpi-visible-products" label={t('portal.visible_products')} value={visible} />
          <KPI testid="portal-kpi-documents" label={t('portal.my_documents')} value={docsCount} />
          <KPI testid="portal-kpi-requests" label={t('portal.my_requests')} value={openRequests} />
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1.4fr_.6fr]">
          <div className="pn-card p-6">
            <div className="font-display text-lg font-semibold text-[#14171C]">{t('portal.quick_actions')}</div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Link to="/portal/catalog" data-testid="portal-go-catalog" className="group flex items-center justify-between rounded-xl border border-[#E4E9F0] p-4 hover:border-[#E2611C]/40 transition">
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#FBEADD] text-[#E2611C]"><Package className="h-4 w-4" /></div>
                  <div className="text-sm font-medium">{t('portal.go_catalog')}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-[#4A4D52] group-hover:text-[#E2611C]" />
              </Link>
              <Link to="/portal/requests" data-testid="portal-go-requests" className="group flex items-center justify-between rounded-xl border border-[#E4E9F0] p-4 hover:border-[#E2611C]/40 transition">
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#FBEADD] text-[#E2611C]"><Send className="h-4 w-4" /></div>
                  <div className="text-sm font-medium">{t('portal.my_requests_view')}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-[#4A4D52] group-hover:text-[#E2611C]" />
              </Link>
            </div>
          </div>

          <div className="pn-card p-6">
            <div className="font-display text-lg font-semibold text-[#14171C] flex items-center gap-2"><FileText className="h-5 w-5 text-[#E2611C]" /> {t('portal.my_documents')}</div>
            <p className="mt-2 text-sm text-[#4A4D52]">{t('product.documents')} – {docsCount}</p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
